import { Box, Button, Divider, Flex, Input, Select, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useAuth } from "../../../Hooks/useAuth";
import { api } from "../../../services/api";
import { getEventCalculatorOptions } from "../../../services/eventCalculator";
import { EditProductType } from "../EditProductType";
import { FiPlus, FiX } from "react-icons/fi";
import swal from "sweetalert";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

export function TypeCard({ allCategories, allProductTypes }) {
  const { user } = useAuth();
  const [productTypeName, setProductTypeName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [eventDrinkCategory, setEventDrinkCategory] = useState("");
  const [categories] = useState(allCategories);
  const [productTypes, setProductTypes] = useState(allProductTypes);
  const [drinkCategories, setDrinkCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    async function fetchDrinkCategories() {
      try {
        const response = await getEventCalculatorOptions();
        setDrinkCategories(response.data.catalog_drink_categories || []);
      } catch (error) {
        console.error("Error fetching catalog drink categories:", error);
      }
    }
    fetchDrinkCategories();
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    if (!productTypeName.trim() || !categoryId) {
      swal({ title: "Atenção", text: "Nome e categoria não podem ficar em branco.", icon: "info" });
      return;
    }
    const response = await api.post("api/v1/product_types", {
      name: productTypeName,
      user_id: user.id,
      category_id: categoryId,
      event_drink_category: eventDrinkCategory,
    }, {
      headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
    });
    if (response.status === 200) {
      setProductTypes(prev => [...prev, response.data]);
      setProductTypeName("");
      setCategoryId("");
      setEventDrinkCategory("");
      setShowForm(false);
      swal({ title: "Criado!", text: "Tipo criado com sucesso.", icon: "success" });
    } else {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
    }
  }

  const updateProductType = (updatedProductType) => {
    setProductTypes(prev => prev.map(pt => pt.id === updatedProductType.id ? updatedProductType : pt));
  };

  const inputStyle = {
    borderRadius: "8px",
    fontSize: "14px",
    border: "1px solid rgba(0,74,51,.2)",
    _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
  };

  return (
    <Box>
      {/* Section header */}
      <Flex align="center" justify="space-between" mb="20px">
        <Box>
          <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
            Catálogo
          </Text>
          <Text fontSize="20px" fontWeight="900" color={GREEN}>
            Tipos de Produto
            <Text as="span" fontSize="14px" fontWeight="600" color="gray.400" ml="8px">
              ({productTypes.length})
            </Text>
          </Text>
        </Box>
        <Button
          size="sm" borderRadius="8px" px="14px"
          bg={showForm ? "gray.100" : GREEN} color={showForm ? "gray.600" : ORANGE}
          fontWeight="700" fontSize="12px" _hover={{ opacity: 0.88 }}
          leftIcon={showForm ? <FiX size={13} /> : <FiPlus size={13} />}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cancelar" : "Novo tipo"}
        </Button>
      </Flex>

      {/* Create form */}
      {showForm && (
        <Box
          bg="white" borderRadius="12px" p="16px 20px" mb="16px"
          border="1px solid rgba(0,74,51,.12)" boxShadow="0 2px 8px rgba(0,74,51,.06)"
        >
          <Text fontSize="12px" fontWeight="700" color="gray.500" mb="10px">Novo tipo de produto</Text>
          <Input
            placeholder="Nome do tipo"
            value={productTypeName}
            onChange={e => setProductTypeName(e.target.value)}
            mb="10px"
            {...inputStyle}
          />
          <Flex gap="10px" mb="10px">
            <Select
              flex="1"
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              {...inputStyle}
            >
              <option value="">Selecione a categoria</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </Select>
            <Select
              flex="1"
              value={eventDrinkCategory}
              onChange={e => setEventDrinkCategory(e.target.value)}
              {...inputStyle}
            >
              <option value="">Categoria da calculadora (opcional)</option>
              {drinkCategories.map(dc => (
                <option key={dc.key} value={dc.key}>{dc.label}</option>
              ))}
            </Select>
          </Flex>
          <Button
            borderRadius="8px" px="20px"
            bg={GREEN} color={ORANGE} fontWeight="700" fontSize="13px"
            _hover={{ opacity: 0.88 }}
            onClick={handleCreate}
          >
            Cadastrar
          </Button>
        </Box>
      )}

      {/* List */}
      <Box bg="white" borderRadius="14px" border="1px solid rgba(0,74,51,.08)" overflow="hidden" boxShadow="0 2px 10px rgba(0,74,51,.04)">
        {productTypes.length === 0 ? (
          <Box py="32px" textAlign="center">
            <Text fontSize="14px" color="gray.400">Nenhum tipo cadastrado</Text>
          </Box>
        ) : (
          productTypes.map((pt, i) => (
            <Box key={pt.id}>
              <Flex
                align="center" justify="space-between"
                px="20px" py="12px"
                _hover={{ bg: "rgba(0,74,51,.025)" }}
                transition="background 0.1s"
              >
                <Flex align="center" gap="10px">
                  <Text fontSize="11px" fontWeight="700" color="gray.300" minW="28px">#{pt.id}</Text>
                  <Text fontSize="14px" fontWeight="600" color="gray.700">{pt.name}</Text>
                  {pt.event_drink_category ? (
                    <Text
                      fontSize="10px" fontWeight="700" color={GREEN} bg="rgba(0,74,51,.08)"
                      borderRadius="99px" px="8px" py="2px" textTransform="uppercase" letterSpacing="0.03em"
                    >
                      {drinkCategories.find(dc => dc.key === pt.event_drink_category)?.label || pt.event_drink_category}
                    </Text>
                  ) : (
                    <Text fontSize="10px" fontWeight="600" color="gray.300">
                      sem categoria da calculadora
                    </Text>
                  )}
                </Flex>
                <EditProductType
                  productTypeId={pt.id}
                  thisProductType={pt}
                  allCategories={categories}
                  allDrinkCategories={drinkCategories}
                  onUpdateProductType={updateProductType}
                />
              </Flex>
              {i < productTypes.length - 1 && <Divider borderColor="rgba(0,74,51,.06)" />}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}
